import { useState, useEffect, useCallback } from 'react';
import { Flex, Box, Progress, Text } from '@chakra-ui/react';
import { useShowToast } from '@/hooks';
import {
  useResumeParserMutation,
  useUploadResumeMutation,
} from '@/store/query/ats-contacts';
import { FileAttachment04, RefreshCw01, Trash01 } from '@kula-ai/icons-core';
import { FileUpload } from '../FileUpload';

const resumeUploadCopy = {
  fileUploadDescription: 'Supported : .pdf, .docx',
  uploadingMessage: 'Uploading resume',
  parsingStartMessage: 'Resume uploaded. Starting parsing',
  processingMessage: 'Processing resume',
  uploadFailedMessage: 'Upload failed. Please try again.',
  parseFailedMessage:
    'We couldn’t use this file. Please choose another one, or fill in manually.',
  unexpectedErrorMessage: 'Unexpected error. Please try again.',
  autofillCompletedMessage:
    'Autofill completed! Please review the information we have filled in below.',
  retryButtonText: 'Retry',
  toastMessages: {
    uploadFailed: 'Resume upload failed',
    corruptFile:
      'This resume file is corrupted. Convert it to a PDF or upload a different version.',
  },
};

const resumeUploadStyles = {
  success: {
    p: '18px',
    w: '100%',
    borderRadius: 'sm',
    border: '1px solid',
    color: 'ats-primary',
    bg: 'ats-bg-tertiary',
    borderColor: 'ats-primary',
  },
  error: {
    bg: 'ats-negative',
    border: 'none',
    color: 'white',
    p: '18px',
    w: '100%',
    borderRadius: 'sm',
  },
};

const POLLING_RETRY_INTERVAL = 3000;
const POLLING_MAX_RETRIES = 20;

export const ContactResumeUpload = ({ setParsedResume, px = '22px' }) => {
  const [resumeParserAction] = useResumeParserMutation();
  const [uploadResume] = useUploadResumeMutation();
  const [file, setFile] = useState(null);
  const [resumeGenerationConfig, setResumeGenConfig] = useState({});
  const [isPolling, setIsPolling] = useState(false);
  const [pollProgressConfig, setPollProgressConfig] = useState({
    value: 0,
    stage: 'idle',
    message: '',
    error: null,
  });
  const toast = useShowToast();

  const handleUpload = (selectedFile) => {
    setFile(selectedFile[0]);
  };

  const getResumeJsonDetails = useCallback(
    async (key) => {
      try {
        const result = await resumeParserAction({ key });
        return result?.data;
      } catch (error) {
        return { error };
      }
    },
    [resumeParserAction]
  );

  const uploadResumeCall = async () => {
    setPollProgressConfig({
      value: 10,
      stage: 'uploading',
      message: resumeUploadCopy.uploadingMessage,
      error: null,
    });

    try {
      const result = await uploadResume({ resume: file });
      if (result?.data?.ats_parsed_resume_id) {
        setResumeGenConfig(result?.data);
        setPollProgressConfig((prev) => ({
          ...prev,
          value: 30,
          stage: 'parsing',
          message: resumeUploadCopy.parsingStartMessage,
        }));
      } else if (result?.error?.[0] === 'err_corrupted_resume') {
        setPollProgressConfig({
          value: 100,
          stage: 'error',
          message: resumeUploadCopy.uploadFailedMessage,
        });
        toast({
          title: resumeUploadCopy.toastMessages.corruptFile,
          status: 'error',
        });
        return;
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      setPollProgressConfig({
        value: 100,
        stage: 'error',
        message: resumeUploadCopy.uploadFailedMessage,
        error,
      });
      toast({
        title: resumeUploadCopy.toastMessages.uploadFailed,
        status: 'error',
      });
    }
  };

  useEffect(() => {
    if (file) {
      uploadResumeCall();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  useEffect(() => {
    const { generation_id } = resumeGenerationConfig;
    const pollResumeDetails = async () => {
      if (generation_id) {
        setIsPolling(true);
        for (let i = 0; i < POLLING_MAX_RETRIES; i++) {
          const result = await getResumeJsonDetails(generation_id);
          const status = result?.parsed_output?.status;

          if (status === 'success') {
            setParsedResume({
              resume: result?.parsed_output?.data,
              config: resumeGenerationConfig,
              file,
            });
            setPollProgressConfig({
              value: 100,
              stage: 'success',
              message: resumeUploadCopy.autofillCompletedMessage,
              error: null,
            });
            break;
          } else if (status === 'failed') {
            setPollProgressConfig({
              value: 100,
              stage: 'error',
              message: resumeUploadCopy.parseFailedMessage,
              error: new Error('Parsing failed'),
            });
            break;
          } else if (status === 'queued' || status === 'retry') {
            setPollProgressConfig((prev) => ({
              ...prev,
              value: 30 + (i + 1) * (70 / POLLING_MAX_RETRIES),
              stage: 'parsing',
              message: resumeUploadCopy.processingMessage,
            }));
            await new Promise((resolve) =>
              setTimeout(resolve, POLLING_RETRY_INTERVAL)
            );
          } else {
            setPollProgressConfig({
              value: 100,
              stage: 'error',
              message: resumeUploadCopy.unexpectedErrorMessage,
              error: new Error('Unexpected status'),
            });
            break;
          }
        }
        setIsPolling(false);
      }
    };
    pollResumeDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeGenerationConfig, getResumeJsonDetails, setParsedResume]);

  const removeFile = () => {
    setFile(null);
    setResumeGenConfig({});
    setPollProgressConfig({
      value: 0,
      stage: 'idle',
      message: '',
      error: null,
    });
  };

  const handleRetry = () => {
    if (file) {
      uploadResumeCall();
    }
  };

  return (
    <Flex w="100%" direction="column">
      <Box px={px} pb="22px" pt="0" w="100%">
        {file ? (
          <Flex direction="column" w="100%">
            <Flex
              p="1em"
              gap="1em"
              border="1px solid #EAEAEA"
              borderRadius="6px"
              alignItems="center"
              justifyContent="space-between"
              w="100%"
            >
              <Flex gap=".5em" alignItems="center">
                <FileAttachment04 size="1em" className="kula-icon" />
                <Box>{file.name}</Box>
              </Flex>
              <Trash01
                size="1em"
                className="kula-icon"
                style={{ cursor: 'pointer' }}
                onClick={removeFile}
              />
            </Flex>

            <Flex flexDir="column" mt="15px" gap="15px">
              <Flex gap="5px">
                <Text
                  {...(pollProgressConfig.stage === 'success'
                    ? resumeUploadStyles.success
                    : pollProgressConfig.stage === 'error'
                      ? resumeUploadStyles.error
                      : {})}
                >
                  {pollProgressConfig.message}
                </Text>
                {pollProgressConfig.stage === 'error' && (
                  <RefreshCw01
                    size="1em"
                    className="kula-icon"
                    style={{ margin: '20px 10px', cursor: 'pointer' }}
                    onClick={handleRetry}
                  />
                )}
              </Flex>

              {pollProgressConfig.stage !== 'success' &&
                pollProgressConfig.stage !== 'error' && (
                  <Progress
                    value={pollProgressConfig.value}
                    size="xs"
                    colorScheme={
                      pollProgressConfig.stage === 'error' ? 'red' : 'green'
                    }
                    hasStripe={isPolling}
                    isAnimated={isPolling}
                  />
                )}
            </Flex>
          </Flex>
        ) : (
          <FileUpload
            acceptedFiles="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            handleUpload={handleUpload}
            showPreview={true}
            description={resumeUploadCopy.fileUploadDescription}
          />
        )}
      </Box>
    </Flex>
  );
};
